/**
 * Package §7 (NavDemo.dc.html's DCLogic): Plan is an expandable group in the sidebar.
 * Pure, so the rules are tested without a DOM; PlanNavGroup draws them.
 *
 * - The Plan button opens the group AND goes to Your plan; pressed while open, it only
 *   closes the group (the page stays).
 * - Going to a Plan page (Your plan, Debt Eliminator, or an address that lights Plan) opens
 *   the group; going anywhere else closes it.
 * - While a Plan page is showing, the parent wears the "inside Plan" fill (#1F3D72),
 *   open or closed; the page's own entry wears the selected fill (#2373E2).
 */
import { REDESIGN_NAV_ITEMS, navItemFor, type NavSection } from "@/config/redesignNavItems";

export const PLAN_GROUP_ID = "plan-links";

const PLAN_ITEM = REDESIGN_NAV_ITEMS.find((i) => i.to === "/plan")!;

function matches(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Whether `pathname` is one of Plan's pages (it lights the Plan item). */
export function inPlan(pathname: string): boolean {
  return navItemFor(pathname) === PLAN_ITEM;
}

/** Plan's entries, in the board's order: Your plan, Debt Eliminator. */
export function planGroupLinks(): NavSection[] {
  return PLAN_ITEM.sections;
}

/** Whether a Plan entry is the page showing. Your plan also stands for the addresses that
 *  light Plan on their way to it (Allocate, Budgets, Goals land on Your plan). */
export function planLinkIsCurrent(section: NavSection, pathname: string): boolean {
  if (matches(pathname, section.to)) return true;
  return (
    section.to === PLAN_ITEM.to && (PLAN_ITEM.alsoLights ?? []).some((p) => matches(pathname, p))
  );
}

/** The group's open state after the route changes: open on a Plan page, closed elsewhere. */
export function planGroupOpenFor(pathname: string): boolean {
  return inPlan(pathname);
}

/** The Plan button pressed: the new open state, and where to go (null: stay put). */
export function planGroupToggle(open: boolean): { open: boolean; goTo: string | null } {
  return open ? { open: false, goTo: null } : { open: true, goTo: PLAN_ITEM.to };
}
